import i18next from "i18next";
import LanguageDetector from "i18next-browser-languagedetector";
import { initReactI18next } from "react-i18next";

const resources = {
  en: {
    translation: {
      // Auth
      "auth.signIn": "Sign in with Google",
      "auth.signOut": "Sign out",
      "auth.logout.title": "Log out",
      "auth.logout.description": "Are you sure to log out?",
      "auth.logout.cancel": "Cancel",
      "auth.logout.confirm": "Log out",
      "auth.logout.deleteAccount": "Do you want to delete your account?",
      "auth.logout.clickHere": "Click here.",
      "auth.deleteAccount.reason.title": "Why Are You Leaving?",
      "auth.deleteAccount.reason.desc":
        "Your feedback helps us improve CROS. Please tell us why you're deleting your account. This step is optional but greatly appreciated.",
      "auth.deleteAccount.reason.specify": "Please specify",
      "auth.deleteAccount.reason.continue": "Continue",
      "auth.deleteAccount.reasons.NotUsed": "Not using the product enough.",
      "auth.deleteAccount.reasons.MissingFeature": "Missing a feature I need.",
      "auth.deleteAccount.reasons.BetterAlternative": "Found a better alternative.",
      "auth.deleteAccount.reasons.Expensive": "Product is too expensive.",
      "auth.deleteAccount.reasons.Bugs": "Technical issues / Bugs.",
      "auth.deleteAccount.reasons.Privacy": "Privacy concerns.",
      "auth.deleteAccount.reasons.Other": "Other (Please specify)",
      "auth.deleteAccount.confirm.title": "Confirm Account Deletion",
      "auth.deleteAccount.confirm.desc":
        "This action is irreversible. Deleting your account will permanently remove all your data, goals, and team associations from CROS. To proceed, please type \"Delete {{email}}\" below.",
      "auth.deleteAccount.confirm.placeholder": "Type \"Delete {{email}}\"",
      "auth.deleteAccount.confirm.matchError":
        "The input you entered doesn't match. Please type \"Delete {{email}}\" exactly to confirm.",
      "auth.deleteAccount.confirm.button": "Delete account",
      "auth.deleteAccount.confirm.cancel": "Cancel",
      "auth.deleteAccount.cannotDelete.title": "Cannot Delete Account",
      "auth.deleteAccount.cannotDelete.desc":
        "You cannot delete your account while you own teams that still have other members. Please remove the remaining members from the following teams first:",
      "auth.deleteAccount.cannotDelete.ok": "OK",
      "auth.welcome": "Welcome to CROS Air",
      "auth.tagline": "Communication is management.",
      "auth.login.title": "Talking is Everything",
      "auth.login.description":
        "Just talk — CROS Air handles the rest. No more stopping to record tasks. While you collaborate, our AI Agent manages the entire flow from task creation to summarizing reports. Stay focused on your vision.",
      "auth.login.button": "Log in with Google Account",
      "auth.signup.title": "Start talking. Start winning.",
      "auth.signup.description":
        "Create your free CROS Air account and let AI handle the rest. Agree to the terms below and jump right in.",
      "auth.signup.tosRequired": "I agree to the Terms of Service(required).",
      "auth.signup.privacyRequired": "I agree to the Privacy Policy(required).",
      "auth.signup.promotionOptional":
        "I want to receive emails about promotions(optional).",
      "auth.signup.agreementError":
        "Please agree to the required agreement(s) by clicking the check box(s) to sign up.",
      "auth.signup.button": "Sign up",
      "auth.termsOfService": "Terms of Service",
      "auth.privacyPolicy": "Privacy Policy",
      "auth.termsOfServiceUrl": "https://cros.team/legal/terms-of-service",
      "auth.privacyPolicyUrl": "https://cros.team/legal/privacy-policy",
      "auth.goToSignup": "Sign up",
      "auth.goToLogin": "Log in",
      "auth.language": "English",
      "auth.changeLanguage": "Change Language",
      "auth.saveChange": "Save Change",

      // Select Team
      "selectTeam.title": "Select a Team",
      "selectTeam.description": "Select a team to access.",
      "selectTeam.myTeams": "My Teams",
      "selectTeam.createNew": "Create a new team",
      "selectTeam.invitedBadge": "Invited",
      "selectTeam.currentBadge": "Current",

      // Create Team
      "createTeam.title": "Create a Team",
      "createTeam.description":
        "Creating a team allows you to invite members, assign responsibilities, and track progress all in one place.",
      "createTeam.nameLabel": "Enter your team name",
      "createTeam.button": "Create",

      // Connect Messenger
      "connectMessenger.title": "Connect Your Workspace Messenger",
      "connectMessenger.description":
        "Link Slack or Discord to automatically capture conversations, track tasks, and turn meetings into AI-powered notes.",
      "connectMessenger.connect": "Connect",
      "connectMessenger.skip": "I'll do this later",

      // Navigation
      "nav.dashboard": "Dashboard",
      "nav.settings": "Settings",
      "nav.selectTeam": "Select Team",
      "nav.teams": "TEAMS",
      "nav.members": "Members",
      "nav.projects": "Projects",
      "nav.switchTeam": "Switch team",
      "nav.teamSettings": "Team settings",
      "nav.switchToEnglish": "Switch to English",
      "nav.switchToKorean": "Switch to Korean",

      // Kanban
      "kanban.todo": "To Do",
      "kanban.doing": "In Progress",
      "kanban.done": "Completed",
      "kanban.addTask": "+ New Thread",
      "kanban.newThread": "+ New Thread",
      "kanban.noTasks": "No thread created",
      "kanban.dropHere": "Drop here",
      "kanban.empty": "No threads yet",

      // Task
      "task.title": "Title",
      "task.titlePlaceholder": "Type the title of the thread",
      "task.titleRequired": "Title is required.",
      "task.description": "Description",
      "task.descriptionPlaceholder": "Type the description of the thread",
      "task.addDescription": "Add description...",
      "task.assignee": "Assignee",
      "task.unassigned": "Unassigned",
      "task.status": "Status",
      "task.subTasks": "Sub-tasks",
      "task.meetingNotes": "Meeting Notes",
      "task.keyAssets": "Key Assets",
      "task.thread": "Thread",
      "task.aiAnalyze": "Analyze",
      "task.create": "Create Thread",
      "task.createDescription":
        "A thread maps to a topic your team will discuss and track in your workspace messenger.",
      "task.threadCreatedTitle": "Thread Created",
      "task.threadCreatedDesc":
        '"{{title}}" has been created. Press "Start Thread" or set status to "In Progress" to open it in your workspace messenger.',
      "task.complete": "complete",
      "task.save": "Save",
      "task.cancel": "Cancel",
      "task.editTitleAria": "Edit thread title",
      "task.editDescriptionAria": "Edit thread description",
      "task.createButton": "Create",
      "task.cancelButton": "Cancel",
      "task.summary": "Summary",
      "task.decided": "Decided",
      "task.pending": "Pending",
      "task.archive": "Archive",
      "task.noNewMessages": "No new messages since the last analysis.",
      "task.archiveConfirmTitle": "Archive This Thread?",
      "task.archiveConfirmDesc":
        "This thread will be hidden from your workspace. You can restore it anytime.",
      "task.restore": "Restore",
      "task.threadRestoredToastTitle": "Thread Restored",
      "task.threadRestoredToastDesc":
        '"{{name}}" is active again.',
      "task.threadRestoreErrorTitle": "Couldn't Restore Thread",
      "task.threadRestoreErrorDesc":
        "Something went wrong. Please try again.",
      "task.archived": "Archived",
      "task.noArchivedTasks": "No archived tasks.",
      "task.showCompleted": "Show completed",
      "task.hideCompleted": "Hide completed",
      "task.showHiddenLinks": "Show Hidden Links",
      "task.hideHiddenLinks": "Hide Hidden Links",
      "task.keyAssetRename": "Rename",
      "task.keyAssetHide": "Hide",
      "task.keyAssetUnhide": "Unhide",
      "task.keyAssetOpenInNewTab": "Click to open the link in a new tab.",
      "task.renameKeyAsset": "Rename Asset",
      "task.renameKeyAssetDescription": "Enter a new name for this asset.",
      "task.keyAssetName": "Name",
      "task.keyAssetNamePlaceholder": "Type the name of the asset",
      "task.properties": "Properties",
      "task.actions": "Actions",
      "task.openThread": "Open Thread",
      "task.startThread": "Start Thread",
      "task.started": "Started",
      "task.notStarted": "Not started",
      "task.threadStartedTitle": "Thread Started",
      "task.threadStartedDesc":
        '"{{title}}" has been opened in your workspace messenger.',
      "task.threadStartErrorTitle": "Couldn't Start Thread",
      "task.threadStartErrorDesc": "Something went wrong. Please try again.",
      "task.changeAssignee": "Change assignee",
      "task.analysisComplete":
        "Analysis complete: {{subTasks}} sub-tasks, {{keyAssets}} assets",
      "task.analysisToastSuccessTitle": "AI Analysis Complete",
      "task.analysisToastSuccessDesc":
        "Progress has been updated based on the latest comments.",
      "task.analysisToastErrorTitle": "Couldn't Analyze Thread",
      "task.archiveToastSuccessTitle": "Thread Archived",
      "task.archiveToastSuccessDesc":
        '"{{title}}" has been moved to the archive.',
      "task.archiveToastErrorTitle": "Couldn't Archive Thread",
      "task.renameToastSuccessTitle": "Asset Renamed",
      "task.renameToastSuccessDesc": 'Renamed to "{{title}}".',
      "task.renameToastErrorTitle": "Couldn't Rename Asset",
      "task.noLinkedThread": "No linked thread.",
      "task.noSubTasks": "No sub-tasks yet.",
      "task.noActiveSubTasks": "No active sub-tasks.",
      "task.noNotes": "No meeting notes.",
      "task.noAssets": "No assets attached.",
      "task.noVisibleAssets": "No visible assets.",
      "task.notFound": "Task not found or you don't have access.",
      "task.messenger": "Messenger",
      // Task status time labels ({{count}} = number of days)
      "task.workingToday": "Working for today",
      "task.workingDay": "Working for 1 day",
      "task.workingDays": "Working for {{count}} days",
      "task.createdToday": "Created today",
      "task.createdDay": "Created 1 day ago",
      "task.createdDays": "Created {{count}} days ago",
      "task.completedOn": "Completed on {{date}}",

      // Team
      "team.create": "Create Team",
      "team.name": "Team Name",
      "team.namePlaceholder": "e.g. Design Team",
      "team.nameRequired": "Team name is required.",
      "team.invite": "Invite Member",
      "team.inviteSubmit": "Invite",
      "team.inviteEmail": "Email Address",
      "team.inviteEmailPlaceholder": "Enter the email address to invite.",
      "team.members": "Members",
      "team.settings": "Team Settings",
      "team.openTeamSettingsMenu": "Open team settings menu",
      "team.settingsRoutePlaceholder":
        "This page is under construction. Full workspace settings will be added in the next steps.",
      "team.createTeam": "Create Team...",
      "team.inviteDescription":
        "Invite a teammate by their email address. They will receive an email invitation if they don't have an account yet.",
      "team.invalidEmail": "Please enter a valid email address.",
      "team.inviteSuccess": "Invitation sent to {{email}}.",
      "team.role.owner": "Owner",
      "team.role.admin": "Admin",
      "team.role.member": "Member",
      "team.status.invited": "Invited",
      "team.messenger": "Messenger",
      "team.workspaceMessenger": "Workspace Messenger",
      "team.messengerServer": "Server",
      "team.messengerStatus": "Status",
      "team.messengerConnected": "Connected",
      "team.messengerNotConnectedShort": "Not connected",
      "team.messengerPermissionsIssue": "Permissions issue",
      "team.resetAccountHintOwner":
        "Reconnects your messenger account and the team workspace connection.",
      "team.resetAccountHintMember": "Reconnects your messenger account only.",
      "team.resetAccountConfirmTitle": "Reset Account",
      "team.resetAccountConfirmDesc":
        "Use this if your workspace messenger connection isn't working. You'll need to reconnect Slack or Discord after resetting.",
      "team.openMessenger": "Open Messenger",
      "team.serverLinked": "{{platform}} server linked",
      "team.serverNotLinked": "No messenger connected",
      "team.changeServer": "Change Server",
      "team.messengerPermissionHint":
        "Only the team owner or admin can change the messenger server.",
      "team.connectMessenger": "Connect a messenger to enable task tracking:",
      "team.connectServer": "Connect Server",
      "team.disconnectServer": "Disconnect",
      "team.archivedTasks": "Archived",
      "team.general": "General",
      "team.timezone": "Timezone",
      "team.timezoneDescription":
        "Set the team's timezone. This is used for scheduling and display purposes.",
      "team.timezoneSaved": "Timezone updated",
      "team.language": "Team Language",
      "team.languageDescription":
        "Choose the language for AI reports and bot messages.",
      "team.languageSaved": "Language updated",
      "team.backToApp": "Back to App",
      "team.archivesNav": "Archives",
      "team.archivesPageTitle": "Archives",
      "team.archivesTabThreads": "Threads",
      "team.archivesTabProjects": "Projects",
      "team.archivesTabMembers": "Members",
      "team.archivesSearchKeywords": "Search keywords here",
      "team.archivedOnLine": "Archived {{date}}",
      "team.saveChanges": "Save Changes",
      "team.settingsSavedTitle": "Settings Saved",
      "team.settingsSavedDesc": "Your team settings have been updated.",
      "team.settingsSaveErrorTitle": "Couldn't Save Settings",
      "team.settingsSaveErrorDesc": "Something went wrong. Please try again.",
      "team.teamArchivedToastTitle": "Team Archived",
      "team.teamArchiveErrorTitle": "Couldn't Archive Team",
      "team.archiveTeam": "Archive Team",
      "team.archiveTeamConfirmTitle": "Archive This Team?",
      "team.archiveTeamConfirmDesc":
        "All members will lose access to this team and its threads.",
      "team.archiveTeamTypeConfirm": 'Type "{{name}}" to confirm.',
      "team.archiveTeamTypeError": "Type the correct team name to archive.",
      "team.archiveTeamLink": "Do you want to archive the team? ",
      "team.archiveTeamClickHere": "Click here",
      "team.restoreTeam": "Restore",
      "team.archivedTeams": "Archived Teams",
      "team.noArchivedTeams": "No archived teams.",
      "team.teamArchived": 'Team "{{name}}" has been moved to the archive.',
      "team.teamRestored": '"{{name}}" is active again.',
      "team.goToTeams": "Go to Teams",
      "team.archived": "Archived",
      "team.archiveMember": "Archive Member",
      "team.archiveMemberConfirmTitle": "Archive User",
      "team.archiveMemberConfirmDesc":
        "Are you sure you want to archive this member? This user will no longer have access to the team.",
      "team.resendInvitation": "Resend Invitation",
      "team.removeInvitationMenu": "Remove Invitation",
      "team.cancelInvitationConfirmTitle": "Remove invitation?",
      "team.cancelInvitationConfirmDesc":
        "This pending invitation will be removed. You can send a new invite to {{email}} later if needed.",
      "team.removeInvitationButton": "Remove",
      "team.invitationRemovedToastTitle": "Invitation removed",
      "team.invitationRemovedToastDesc":
        "The invitation for {{email}} was removed.",
      "team.memberInviteToastTitle": "Member Invited",
      "team.memberInviteToastDesc":
        "An invitation has been sent to '{{email}}'.",
      "team.invitationResentToastTitle": "Invitation Resent",
      "team.invitationResentToastDesc":
        "An invitation has been resent to '{{email}}'.",
      "team.memberArchivedToastTitle": "Member Archived",
      "team.memberArchivedToastDesc":
        "{{name}} has been archived from the team.",
      "team.restoreMember": "Restore",
      "team.restoreMemberConfirmTitle": "Restore This Member?",
      "team.restoreMemberConfirmDesc":
        '"{{email}}" is archived. Restoring will grant them access to this team again.',
      "team.noArchivedMembers": "No archived members.",
      "team.memberArchived": "Member has been archived.",
      "team.inviteFailed": "Could not send the invitation.",
      "team.inviteErrorAlreadyMember": "This user is already a member of the team.",
      "team.inviteErrorMemberArchived": "This member is archived. Please restore them from the Archived tab.",
      "team.inviteErrorMemberArchivedAskAdmin":
        "This email belongs to an archived member. Ask an owner or admin to restore them.",
      "team.inviteErrorRateLimited": "Please wait a moment before re-sending the invitation.",
      "team.inviteErrorInternal": "Something went wrong while sending this invitation.",
      "team.memberRestored": "Member has been restored.",
      "team.memberRestoredToastTitle": "Member Restored",
      "team.memberRestoredToastDesc": '"{{name}}" is active again.',
      "team.archivedProjects": "Archived Projects",
      "team.archivedMembers": "Archived Members",

      // Project
      "project.create": "Create Project",
      "project.createButton": "Create",
      "project.createSuccess": 'Project "{{name}}" created.',
      "project.name": "Project Name",
      "project.nameRequired": "Enter the title to create.",
      "project.namePlaceholder": "Type the title of the project",
      "project.description": "Description",
      "project.descriptionPlaceholder": "Type the description of the project",
      "project.linkChannel": "Link Channel",
      "project.channelRequired": "Select a channel to connect.",
      "project.channelAlreadyLinked": "(already linked)",
      "project.noProjects": "No projects yet. Create one to get started.",
      "project.yourProjects": "Your Projects",
      "project.selectTeam": "Select a team to get started",
      "project.chooseProjectSidebar":
        "Choose or create a project from the sidebar to view tasks.",
      "project.channelDescription":
        "A project maps to a channel where your team communicates.",
      "project.channel": "Channel",
      "project.channelIdHelper":
        "Link a messenger server in team settings to select channels from a list.",
      "project.emptyStartTitle": "No Projects Created Yet",
      "project.archiveProject": "Archive",
      "project.archiveProjectConfirmTitle": "Archive This Project?",
      "project.archiveProjectConfirmDesc":
        "This project will be hidden from your workspace. You can restore it anytime.",
      "project.restoreProject": "Restore",
      "project.noArchivedProjects": "No archived projects.",
      "project.projectArchived":
        'Project Archived — "{{name}}" has been moved to the archive.',
      "project.projectRestored": 'Project "{{name}}" has been restored.',
      "project.projectRestoredToastTitle": "Project Restored",
      "project.projectRestoredToastDesc":
        '"{{name}}" is active again.',

      // Messenger
      "messenger.accounts": "Messenger Accounts",
      "messenger.connect": "Connect Messenger",
      "messenger.connectDesc":
        "Connect your messenger account to enable thread tracking and AI analysis.",
      "messenger.notConnected": "Not connected",
      "messenger.connectWith": "Connect with {{platform}}",
      "messenger.comingSoon": "Coming soon",
      "messenger.accountConnected": "Messenger account connected successfully.",
      "messenger.accountError":
        "Failed to connect messenger account. Please try again.",
      "messenger.emailMismatch":
        "The messenger account email does not match your account email. Please use a messenger account with the same email.",

      // Discord (platform-specific)
      "discord.connected": "Connected",
      "discord.userId": "User ID",
      "discord.nickname": "Nickname",
      "discord.connectWithDiscord": "Connect with Discord",
      "discord.resetAccount": "Reset Account",
      "discord.howToFind": "How to find a channel ID",
      "discord.howToFindGuide":
        "Discord → Right-click a channel → Copy Channel ID\n(Requires Developer Mode: Settings → Advanced → Developer Mode)",
      "discord.invalidChannelId": "Channel ID must be a 17–20 digit number.",
      "discord.channelIdPlaceholder": "e.g. 1234567890123456789",

      // Slack (platform-specific)
      "slack.howToFind": "How to find a channel ID",
      "slack.howToFindGuide":
        "Slack → Right-click a channel → View channel details → Copy the Channel ID at the bottom",
      "slack.invalidChannelId":
        "Channel ID must be an alphanumeric Slack channel ID.",
      "slack.channelIdPlaceholder": "e.g. C01ABCDEF",

      // Onboarding
      "onboarding.duplicateServer":
        "This server is already linked to another team. Please unlink it first or choose a different server.",
      "onboarding.oauthError": "Failed to connect messenger. Please try again.",
      "onboarding.serverLinkFailed":
        "Failed to link the server. Your previous connection has been preserved.",
      "onboarding.permissionBanner":
        "The CROS Air bot is missing required permissions in your server. Some features may not work correctly.",
      "onboarding.emptyStateGuide":
        "Your messenger is connected! Create a project and link it to a channel to start tracking conversations.",
      "onboarding.platformDiscord": "Discord",
      "onboarding.platformSlack": "Slack",
      "onboarding.step4GuildName": "Server: {{name}}",

      // Common
      "common.loading": "Loading...",
      "common.error": "Something went wrong",
      "common.save": "Save",
      "common.cancel": "Cancel",
      "common.delete": "Delete",
      "common.undo": "Undo",
      "common.close": "Close",
      "common.moreActions": "More actions",
      "common.backToDashboard": "Back to dashboard",
      "common.selectChannel": "Select a channel",
      "common.openInMessenger": "Open in messenger",
      "common.moveTo": "Move to {{status}}",
      "common.next": "Next",
      "common.seeMore": "See More",
      "common.showMore": "Show More",
      "common.showLess": "Show Less",
      "project.untitled": "Untitled Project",
    },
  },
  ko: {
    translation: {
      // Auth
      "auth.signIn": "Google로 로그인",
      "auth.signOut": "로그아웃",
      "auth.logout.title": "로그아웃",
      "auth.logout.description": "로그아웃 하시겠습니까?",
      "auth.logout.cancel": "취소",
      "auth.logout.confirm": "로그아웃",
      "auth.logout.deleteAccount": "계정을 삭제하시겠습니까?",
      "auth.logout.clickHere": "여기를 클릭하세요.",
      "auth.deleteAccount.reason.title": "떠나시는 이유가 무엇인가요?",
      "auth.deleteAccount.reason.desc":
        "여러분의 피드백은 CROS를 개선하는 데 도움이 됩니다. 계정을 삭제하시는 이유를 알려주세요. 선택 사항이지만 큰 도움이 됩니다.",
      "auth.deleteAccount.reason.specify": "내용을 입력해주세요",
      "auth.deleteAccount.reason.continue": "계속",
      "auth.deleteAccount.reasons.NotUsed": "제품을 충분히 사용하지 않음",
      "auth.deleteAccount.reasons.MissingFeature": "필요한 기능이 없음",
      "auth.deleteAccount.reasons.BetterAlternative": "더 나은 대안을 찾음",
      "auth.deleteAccount.reasons.Expensive": "제품이 너무 비쌈",
      "auth.deleteAccount.reasons.Bugs": "기술적 문제 / 버그",
      "auth.deleteAccount.reasons.Privacy": "개인 정보 보호 우려",
      "auth.deleteAccount.reasons.Other": "기타 (직접 입력)",
      "auth.deleteAccount.confirm.title": "계정 삭제 확인",
      "auth.deleteAccount.confirm.desc":
        "이 작업은 되돌릴 수 없습니다. 계정을 삭제하면 CROS의 모든 데이터, 목표, 팀 연결이 영구적으로 삭제됩니다. 계속하려면 아래에 \"Delete {{email}}\"를 입력하세요.",
      "auth.deleteAccount.confirm.placeholder": "\"Delete {{email}}\" 입력",
      "auth.deleteAccount.confirm.matchError":
        "입력한 내용이 일치하지 않습니다. 확인하려면 \"Delete {{email}}\"을 정확히 입력하세요.",
      "auth.deleteAccount.confirm.button": "계정 삭제",
      "auth.deleteAccount.confirm.cancel": "취소",
      "auth.deleteAccount.cannotDelete.title": "계정을 삭제할 수 없습니다",
      "auth.deleteAccount.cannotDelete.desc":
        "다른 멤버가 남아있는 팀을 소유한 상태로는 계정을 삭제할 수 없습니다. 아래 팀의 나머지 멤버를 먼저 제거해 주세요:",
      "auth.deleteAccount.cannotDelete.ok": "확인",
      "auth.welcome": "CROS Air에 오신 것을 환영합니다",
      "auth.tagline": "소통이 곧 관리다.",
      "auth.login.title": "대화가 곧 전부입니다",
      "auth.login.description":
        "대화만 나누세요 — 업무 기록을 위해 대화를 멈출 필요가 없습니다. 팀원들과 자유롭게 소통하면, AI 에이전트가 태스크 생성부터 리포트 요약까지 업무 관리의 전 과정을 자동으로 처리합니다. 당신은 팀의 방향에만 집중하세요.",
      "auth.login.button": "Google 계정으로 로그인",
      "auth.signup.title": "대화를 시작하세요. 성과를 만드세요.",
      "auth.signup.description":
        "무료 CROS Air 계정을 만들고 AI가 나머지를 처리하게 하세요. 아래 약관에 동의하고 바로 시작하세요.",
      "auth.signup.tosRequired": "서비스 이용약관에 동의합니다(필수).",
      "auth.signup.privacyRequired": "개인정보처리방침에 동의합니다(필수).",
      "auth.signup.promotionOptional":
        "프로모션 이메일 수신에 동의합니다(선택).",
      "auth.signup.agreementError":
        "회원가입을 위해 필수 동의 항목에 체크해주세요.",
      "auth.signup.button": "회원가입",
      "auth.termsOfService": "서비스 이용약관",
      "auth.privacyPolicy": "개인정보처리방침",
      "auth.termsOfServiceUrl": "https://cros.team/ko/legal/terms-of-service",
      "auth.privacyPolicyUrl": "https://cros.team/ko/legal/privacy-policy",
      "auth.goToSignup": "회원가입",
      "auth.goToLogin": "로그인",
      "auth.language": "한국어",
      "auth.changeLanguage": "언어 변경",
      "auth.saveChange": "변경 저장",

      // Select Team
      "selectTeam.title": "팀 선택",
      "selectTeam.description": "접근할 팀을 선택하세요.",
      "selectTeam.myTeams": "내 팀",
      "selectTeam.createNew": "새 팀 만들기",
      "selectTeam.invitedBadge": "초대됨",
      "selectTeam.currentBadge": "현재",

      // Create Team
      "createTeam.title": "팀 만들기",
      "createTeam.description":
        "팀을 만들면 멤버를 초대하고, 역할을 배정하고, 진행 상황을 한곳에서 추적할 수 있습니다.",
      "createTeam.nameLabel": "팀 이름을 입력하세요",
      "createTeam.button": "만들기",

      // Connect Messenger
      "connectMessenger.title": "워크스페이스 메신저 연결",
      "connectMessenger.description":
        "Slack 또는 Discord를 연결하여 대화를 자동으로 캡처하고, 업무를 추적하고, 회의를 AI 기반 노트로 변환하세요.",
      "connectMessenger.connect": "연결",
      "connectMessenger.skip": "나중에 할게요",

      // Navigation
      "nav.dashboard": "대시보드",
      "nav.settings": "설정",
      "nav.selectTeam": "팀 선택",
      "nav.teams": "팀",
      "nav.members": "멤버",
      "nav.projects": "프로젝트",
      "nav.switchTeam": "팀 전환",
      "nav.teamSettings": "팀 설정",
      "nav.switchToEnglish": "영어로 변경",
      "nav.switchToKorean": "한국어로 변경",

      // Kanban
      "kanban.todo": "할 일",
      "kanban.doing": "진행 중",
      "kanban.done": "완료",
      "kanban.addTask": "업무 추가",
      "kanban.newThread": "+ 새 스레드",
      "kanban.noTasks": "아직 업무가 없습니다",
      "kanban.dropHere": "여기에 드롭",

      // Task
      "task.title": "제목",
      "task.titlePlaceholder": "스레드 제목을 입력하세요",
      "task.titleRequired": "제목은 필수입니다.",
      "task.description": "설명",
      "task.descriptionPlaceholder": "스레드 설명을 입력하세요",
      "task.addDescription": "설명 추가...",
      "task.assignee": "담당자",
      "task.status": "상태",
      "task.subTasks": "하위 업무",
      "task.meetingNotes": "회의록",
      "task.keyAssets": "핵심 자료",
      "task.thread": "스레드",
      "task.aiAnalyze": "분석",
      "task.create": "업무 만들기",
      "task.createDescription":
        "스레드는 팀이 메신저에서 논의하고 추적할 주제와 매핑됩니다.",
      "task.threadCreatedTitle": "스레드가 생성되었습니다",
      "task.threadCreatedDesc":
        '"{{title}}" 스레드가 생성되었습니다. "스레드 시작"을 누르거나 상태를 "진행 중"으로 변경하면 워크스페이스 메신저에서 열 수 있습니다.',
      "task.complete": "완료",
      "task.save": "저장",
      "task.cancel": "취소",
      "task.editTitleAria": "스레드 제목 편집",
      "task.editDescriptionAria": "스레드 설명 편집",
      "task.createButton": "만들기",
      "task.cancelButton": "취소",
      "task.summary": "요약",
      "task.decided": "결정 사항",
      "task.pending": "미결 사항",
      "task.archive": "보관",
      "task.noNewMessages": "마지막 분석 이후 새 메시지가 없습니다.",
      "task.archiveConfirmTitle": "이 스레드를 보관할까요?",
      "task.archiveConfirmDesc":
        "워크스페이스에서 이 스레드가 숨겨집니다. 언제든지 복원할 수 있습니다.",
      "task.restore": "복원",
      "task.threadRestoredToastTitle": "스레드 복원됨",
      "task.threadRestoredToastDesc":
        '"{{name}}" 스레드가 다시 활성 상태가 되었습니다.',
      "task.threadRestoreErrorTitle": "스레드를 복원할 수 없습니다",
      "task.threadRestoreErrorDesc":
        "문제가 발생했습니다. 다시 시도해주세요.",
      "task.archived": "보관됨",
      "task.noArchivedTasks": "보관된 업무가 없습니다.",
      "task.unassigned": "미배정",
      "task.showCompleted": "완료된 항목 보기",
      "task.hideCompleted": "완료된 항목 숨기기",
      "task.showHiddenLinks": "숨겨진 링크 보기",
      "task.hideHiddenLinks": "숨겨진 링크 숨기기",
      "task.keyAssetRename": "이름 변경",
      "task.keyAssetHide": "숨기기",
      "task.keyAssetUnhide": "숨기기 해제",
      "task.keyAssetOpenInNewTab": "클릭하면 새 탭에서 링크가 열립니다.",
      "task.renameKeyAsset": "자산 이름 바꾸기",
      "task.renameKeyAssetDescription": "이 자산의 새 이름을 입력하세요.",
      "task.keyAssetName": "이름",
      "task.keyAssetNamePlaceholder": "자산 이름을 입력하세요",
      "task.properties": "필드",
      "task.actions": "작업",
      "task.openThread": "스레드 열기",
      "task.startThread": "스레드 시작",
      "task.started": "시작됨",
      "task.notStarted": "시작 전",
      "task.threadStartedTitle": "스레드가 시작되었습니다",
      "task.threadStartedDesc":
        '"{{title}}" 스레드가 워크스페이스 메신저에서 열렸습니다.',
      "task.threadStartErrorTitle": "스레드를 시작할 수 없습니다",
      "task.threadStartErrorDesc": "문제가 발생했습니다. 다시 시도해 주세요.",
      "task.changeAssignee": "담당자 변경",
      "task.analysisComplete":
        "분석 완료: 하위 업무 {{subTasks}}개, 자산 {{keyAssets}}개",
      "task.analysisToastSuccessTitle": "AI 분석 완료",
      "task.analysisToastSuccessDesc":
        "최신 코멘트를 기반으로 진행 상황이 업데이트되었습니다.",
      "task.analysisToastErrorTitle": "스레드를 분석할 수 없습니다",
      "task.archiveToastSuccessTitle": "스레드 보관 완료",
      "task.archiveToastSuccessDesc": '"{{title}}" 스레드가 보관되었습니다.',
      "task.archiveToastErrorTitle": "스레드를 보관할 수 없습니다",
      "task.renameToastSuccessTitle": "자산 이름 변경 완료",
      "task.renameToastSuccessDesc": '"{{title}}"(으)로 변경되었습니다.',
      "task.renameToastErrorTitle": "자산 이름을 변경할 수 없습니다",
      "task.noLinkedThread": "연동된 스레드가 없습니다.",
      "task.noSubTasks": "아직 하위 업무가 없습니다.",
      "task.noActiveSubTasks": "활성 하위 업무가 없습니다.",
      "task.noNotes": "아직 분석된 회의록이 없습니다.",
      "task.noAssets": "첨부된 자료가 없습니다.",
      "task.noVisibleAssets": "보이는 자료가 없습니다.",
      "task.notFound": "업무를 찾을 수 없거나 권한이 없습니다.",
      "task.messenger": "메신저",
      "task.workingToday": "오늘부터 진행 중",
      "task.workingDay": "1일째 진행 중",
      "task.workingDays": "{{count}}일째 진행 중",
      "task.createdToday": "오늘 생성됨",
      "task.createdDay": "1일 전에 생성됨",
      "task.createdDays": "{{count}}일 전에 생성됨",
      "task.completedOn": "{{date}}에 완료됨",

      // Team
      "team.create": "팀 만들기",
      "team.name": "팀 이름",
      "team.namePlaceholder": "예: 디자인팀",
      "team.nameRequired": "팀 이름을 입력해주세요.",
      "team.invite": "멤버 초대",
      "team.inviteSubmit": "초대",
      "team.inviteEmail": "이메일 주소",
      "team.inviteEmailPlaceholder": "초대할 이메일 주소를 입력하세요.",
      "team.members": "멤버",
      "team.settings": "팀 설정",
      "team.openTeamSettingsMenu": "팀 설정 메뉴 열기",
      "team.settingsRoutePlaceholder":
        "이 페이지는 준비 중입니다. 다음 단계에서 워크스페이스 설정을 채워 넣습니다.",
      "team.createTeam": "팀 만들기...",
      "team.teamSettings": "팀 설정",
      "team.inviteDescription":
        "팀원의 이메일로 초대하세요. 아직 계정이 없으면 초대 메일이 발송됩니다.",
      "team.invalidEmail": "올바른 이메일 주소를 입력해 주세요.",
      "team.inviteSuccess": "{{email}}(으)로 초대를 보냈습니다.",
      "team.role.owner": "오너",
      "team.role.admin": "관리자",
      "team.role.member": "멤버",
      "team.status.invited": "초대됨",
      "team.messenger": "메신저",
      "team.workspaceMessenger": "워크스페이스 메신저",
      "team.messengerServer": "서버",
      "team.messengerStatus": "상태",
      "team.messengerConnected": "연결됨",
      "team.messengerNotConnectedShort": "연결 안 됨",
      "team.messengerPermissionsIssue": "권한 문제",
      "team.resetAccountHintOwner":
        "메신저 계정과 팀 워크스페이스 연결을 다시 설정합니다.",
      "team.resetAccountHintMember": "개인 메신저 계정만 다시 연결합니다.",
      "team.resetAccountConfirmTitle": "계정 재설정",
      "team.resetAccountConfirmDesc":
        "워크스페이스 메신저 연결이 동작하지 않을 때 사용하세요. 재설정 후 Slack 또는 Discord를 다시 연결해야 합니다.",
      "team.openMessenger": "메신저 열기",
      "team.serverLinked": "{{platform}} 서버 연동됨",
      "team.serverNotLinked": "연동된 메신저 없음",
      "team.changeServer": "서버 변경",
      "team.connectServer": "서버 연결",
      "team.messengerPermissionHint":
        "팀 소유자 또는 관리자만 메신저 서버를 변경할 수 있습니다.",
      "team.connectMessenger": "업무 추적을 위해 메신저를 연결하세요:",
      "team.disconnectServer": "연결 해제",
      "team.archivedTasks": "보관됨",
      "team.general": "일반",
      "team.timezone": "타임존",
      "team.timezoneDescription":
        "팀의 타임존을 설정합니다. 일정 관리 및 표시에 사용됩니다.",
      "team.timezoneSaved": "타임존이 업데이트되었습니다",
      "team.language": "팀 언어",
      "team.languageDescription":
        "AI 리포트 및 봇 메시지에 사용할 언어를 선택하세요.",
      "team.languageSaved": "언어가 업데이트되었습니다",
      "team.backToApp": "앱으로 돌아가기",
      "team.archivesNav": "보관함",
      "team.archivesPageTitle": "보관함",
      "team.archivesTabThreads": "스레드",
      "team.archivesTabProjects": "프로젝트",
      "team.archivesTabMembers": "멤버",
      "team.archivesSearchKeywords": "키워드 검색",
      "team.archivedOnLine": "{{date}} 보관됨",
      "team.saveChanges": "변경 사항 저장",
      "team.settingsSavedTitle": "설정이 저장되었습니다",
      "team.settingsSavedDesc": "팀 설정이 업데이트되었습니다.",
      "team.settingsSaveErrorTitle": "설정을 저장할 수 없습니다",
      "team.settingsSaveErrorDesc": "문제가 발생했습니다. 다시 시도해 주세요.",
      "team.teamArchivedToastTitle": "팀이 보관되었습니다",
      "team.teamArchiveErrorTitle": "팀을 보관할 수 없습니다",
      "team.archiveTeam": "팀 보관",
      "team.archiveTeamConfirmTitle": "이 팀을 보관할까요?",
      "team.archiveTeamConfirmDesc":
        "모든 멤버가 이 팀과 스레드에 대한 접근 권한을 잃게 됩니다.",
      "team.archiveTeamTypeConfirm": '"{{name}}"을(를) 입력하여 확인하세요.',
      "team.archiveTeamTypeError": "올바른 팀 이름을 입력하세요.",
      "team.archiveTeamLink": "팀을 보관하시겠습니까? ",
      "team.archiveTeamClickHere": "여기를 클릭하세요",
      "team.restoreTeam": "복원",
      "team.archivedTeams": "보관된 팀",
      "team.noArchivedTeams": "보관된 팀이 없습니다.",
      "team.teamArchived": '"{{name}}" 팀이 보관되었습니다.',
      "team.teamRestored": '"{{name}}" 팀이 다시 활성화되었습니다.',
      "team.goToTeams": "팀으로 이동",
      "team.archived": "보관",
      "team.archiveMember": "멤버 보관",
      "team.archiveMemberConfirmTitle": "사용자 보관",
      "team.archiveMemberConfirmDesc":
        "이 멤버를 보관할까요? 이 사용자는 더 이상 팀에 접근할 수 없습니다.",
      "team.resendInvitation": "초대 다시 보내기",
      "team.removeInvitationMenu": "초대 취소",
      "team.cancelInvitationConfirmTitle": "초대를 취소할까요?",
      "team.cancelInvitationConfirmDesc":
        "대기 중인 초대가 삭제됩니다. 나중에 {{email}}에게 새 초대를 보낼 수 있습니다.",
      "team.removeInvitationButton": "삭제",
      "team.invitationRemovedToastTitle": "초대가 삭제되었습니다",
      "team.invitationRemovedToastDesc":
        "{{email}}에 대한 초대가 삭제되었습니다.",
      "team.memberInviteToastTitle": "멤버 초대됨",
      "team.memberInviteToastDesc": "{{email}}(으)로 초대 메일을 보냈습니다.",
      "team.invitationResentToastTitle": "초대 재발송",
      "team.invitationResentToastDesc":
        "{{email}}(으)로 초대 메일을 다시 보냈습니다.",
      "team.memberArchivedToastTitle": "멤버 보관됨",
      "team.memberArchivedToastDesc":
        "{{name}}님이 팀에서 보관 처리되었습니다.",
      "team.restoreMember": "복원",
      "team.restoreMemberConfirmTitle": "이 멤버를 복원할까요?",
      "team.restoreMemberConfirmDesc":
        '"{{email}}" 계정은 보관된 상태입니다. 복원하면 이 팀에 다시 접근할 수 있습니다.',
      "team.noArchivedMembers": "보관된 멤버가 없습니다.",
      "team.memberArchived": "멤버가 보관되었습니다.",
      "team.inviteFailed": "초대를 보낼 수 없습니다.",
      "team.inviteErrorAlreadyMember": "이미 팀에 속한 사용자입니다.",
      "team.inviteErrorMemberArchived": "보관된 멤버입니다. 보관함에서 복구해 주세요.",
      "team.inviteErrorMemberArchivedAskAdmin":
        "보관된 멤버의 이메일입니다. 오너 또는 관리자에게 복구를 요청해 주세요.",
      "team.inviteErrorRateLimited": "잠시 후 다시 초대를 보내주세요.",
      "team.inviteErrorInternal": "초대를 보내는 중 오류가 발생했습니다.",
      "team.memberRestored": "멤버가 복원되었습니다.",
      "team.memberRestoredToastTitle": "멤버 복원됨",
      "team.memberRestoredToastDesc": '"{{name}}"님이 다시 활성 상태가 되었습니다.',
      "team.archivedProjects": "보관된 프로젝트",
      "team.archivedMembers": "보관된 멤버",

      // Project
      "project.create": "프로젝트 만들기",
      "project.createButton": "만들기",
      "project.createSuccess": '프로젝트 "{{name}}"이(가) 생성되었습니다.',
      "project.name": "프로젝트 이름",
      "project.nameRequired": "생성할 제목을 입력하세요.",
      "project.namePlaceholder": "프로젝트 제목을 입력하세요",
      "project.description": "설명",
      "project.descriptionPlaceholder": "프로젝트 설명을 입력하세요",
      "project.linkChannel": "채널 연동",
      "project.channelRequired": "연결할 채널을 선택하세요.",
      "project.channelAlreadyLinked": "(이미 연결됨)",
      "project.noProjects": "아직 프로젝트가 없습니다. 하나 만들어보세요.",
      "project.yourProjects": "내 프로젝트",
      "project.selectTeam": "시작하려면 팀을 선택하세요",
      "project.chooseProjectSidebar":
        "업무를 보려면 사이드바에서 프로젝트를 선택하거나 만드세요.",
      "project.channelDescription":
        "프로젝트는 팀이 소통하는 채널에 매핑됩니다.",
      "project.channel": "채널",
      "project.channelIdHelper":
        "팀 설정에서 메신저 서버를 연결하면 채널 목록에서 선택할 수 있습니다.",
      "project.emptyStartTitle": "아직 생성된 프로젝트가 없습니다",
      "project.archiveProject": "보관",
      "project.archiveProjectConfirmTitle": "이 프로젝트를 보관할까요?",
      "project.archiveProjectConfirmDesc":
        "워크스페이스에서 이 프로젝트가 숨겨집니다. 언제든지 복원할 수 있습니다.",
      "project.restoreProject": "복원",
      "project.noArchivedProjects": "보관된 프로젝트가 없습니다.",
      "project.projectArchived": '"{{name}}" 프로젝트가 보관되었습니다.',
      "project.projectRestored": '"{{name}}" 프로젝트가 복원되었습니다.',
      "project.projectRestoredToastTitle": "프로젝트 복원됨",
      "project.projectRestoredToastDesc":
        '"{{name}}" 프로젝트가 다시 활성 상태로 복원되었습니다.',

      // Messenger
      "messenger.accounts": "메신저 계정",
      "messenger.connect": "메신저 연동",
      "messenger.connectDesc":
        "메신저 계정을 연동하면 스레드 추적과 AI 분석이 활성화됩니다.",
      "messenger.notConnected": "미연동",
      "messenger.connectWith": "{{platform}} 연동하기",
      "messenger.comingSoon": "준비 중",
      "messenger.accountConnected": "메신저 계정이 연결되었습니다.",
      "messenger.accountError":
        "메신저 계정 연결에 실패했습니다. 다시 시도해주세요.",
      "messenger.emailMismatch":
        "메신저 계정의 이메일이 현재 계정 이메일과 일치하지 않습니다. 동일한 이메일의 메신저 계정을 사용해주세요.",

      // Discord (platform-specific)
      "discord.connected": "연동됨",
      "discord.userId": "사용자 ID",
      "discord.nickname": "닉네임",
      "discord.connectWithDiscord": "디스코드로 연결",
      "discord.resetAccount": "계정 재설정",
      "discord.howToFind": "채널 ID 찾는 방법",
      "discord.howToFindGuide":
        "Discord → 채널 우클릭 → 채널 ID 복사\n(개발자 모드 필요: 설정 → 고급 → 개발자 모드)",
      "discord.invalidChannelId": "채널 ID는 17~20자리 숫자여야 합니다.",
      "discord.channelIdPlaceholder": "예: 1234567890123456789",

      // Slack (platform-specific)
      "slack.howToFind": "채널 ID 찾는 방법",
      "slack.howToFindGuide":
        "Slack → 채널 우클릭 → 채널 세부정보 보기 → 하단의 채널 ID 복사",
      "slack.invalidChannelId": "채널 ID는 영문/숫자 조합이어야 합니다.",
      "slack.channelIdPlaceholder": "예: C01ABCDEF",

      // Onboarding
      "onboarding.duplicateServer":
        "이 서버는 이미 다른 팀에 연결되어 있습니다. 먼저 연결을 해제하거나 다른 서버를 선택해주세요.",
      "onboarding.oauthError": "메신저 연결에 실패했습니다. 다시 시도해주세요.",
      "onboarding.serverLinkFailed":
        "서버 연결에 실패했습니다. 기존 연결 정보가 유지됩니다.",
      "onboarding.permissionBanner":
        "CROS Air 봇에 서버의 필수 권한이 없습니다. 일부 기능이 정상적으로 작동하지 않을 수 있습니다.",
      "onboarding.emptyStateGuide":
        "메신저가 연결되었습니다! 프로젝트를 만들고 채널을 연결하여 대화 추적을 시작하세요.",
      "onboarding.platformDiscord": "Discord",
      "onboarding.platformSlack": "Slack",
      "onboarding.step4GuildName": "서버: {{name}}",

      // Common
      "common.loading": "로딩 중...",
      "common.error": "오류가 발생했습니다",
      "common.save": "저장",
      "common.cancel": "취소",
      "common.delete": "삭제",
      "common.undo": "실행 취소",
      "common.edit": "수정",
      "common.close": "닫기",
      "common.moreActions": "더보기",
      "common.backToDashboard": "대시보드로 돌아가기",
      "common.selectChannel": "채널을 선택하세요",
      "common.profile": "프로필",
      "common.openInMessenger": "메신저에서 열기",
      "common.moveTo": "{{status}}(으)로 이동",
      "common.next": "다음",
      "common.seeMore": "더 보기",
      "common.showMore": "더 보기",
      "common.showLess": "숨기기",
      "project.untitled": "제목 없는 프로젝트",
    },
  },
};

i18next
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: "en",
    supportedLngs: ["en", "ko"],
    interpolation: { escapeValue: false },
    detection: {
      order: ["cookie", "htmlTag", "navigator"],
      lookupCookie: "lang",
      caches: ["cookie"],
      cookieMinutes: 60 * 24 * 365,
      cookieOptions: { path: "/", sameSite: "lax" },
    },
  });

export default i18next;
